import React from 'react';
import { AIProvider, Conversation } from '../types/ai';
import { Assistant } from '../types/assistant';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Plus, MoreVertical, Trash2, Lock, Globe, Users } from 'lucide-react';

interface NewAISidebarProps {
  providers: AIProvider[];
  activeProvider: string | null;
  onProviderSelect: (providerId: string) => void;
  assistants: Assistant[];
  onAssistantSelect: (assistant: Assistant) => void;
  onCreateAssistant: () => void;
  conversations: Conversation[];
  activeConversationId?: string;
  onConversationSelect: (conversationId: string) => void;
  onNewConversation: (providerId: string) => void;
  onDeleteConversation: (conversationId: string) => void; // Nova prop
}

export function NewAISidebar({ 
  providers, 
  activeProvider, 
  onProviderSelect,
  assistants,
  onAssistantSelect,
  onCreateAssistant,
  conversations,
  activeConversationId,
  onConversationSelect,
  onNewConversation,
  onDeleteConversation
}: NewAISidebarProps) {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  return (
    <div className="w-64 bg-gray-50 border-r border-gray-200 h-full flex flex-col overflow-hidden">
      {/* Seção de IAs - Fixa */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <h2 className="text-sm font-medium text-gray-900 mb-4">Inteligência Artificial</h2>
        
        <div className="space-y-1">
          {providers.map((provider) => (
            <Button
              key={provider.id}
              variant={activeProvider === provider.id ? "secondary" : "ghost"}
              className="w-full justify-start text-sm font-medium h-9 px-2"
              onClick={() => onProviderSelect(provider.id)}
            >
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-2">
                {provider.id === 'openai' ? (
                  <img 
                    src="/gpt-logo.png?v=1" 
                    alt="GPT Logo" 
                    className="w-4 h-4 object-contain" 
                  />
                ) : provider.id === 'anthropic' ? (
                  <img 
                    src="/claude-logo.png?v=1" 
                    alt="Claude Logo" 
                    className="w-4 h-4 object-contain"
                  />
                ) : provider.id === 'google' ? (
                  <img 
                    src="/gemini-logo.png?v=1" 
                    alt="Gemini Logo" 
                    className="w-4 h-4 object-contain"
                  />
                ) : (
                  <span className="text-sm">{provider.logo}</span>
                )}
              </div>
              <span className="truncate">{provider.name}</span>
            </Button>
          ))}
        </div>
      </div>
      
      {/* Seção de Agentes - Fixa */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-gray-900">Agentes</h2>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-blue-600 hover:bg-blue-50"
            onClick={onCreateAssistant}
          >
            <Plus className="w-3 h-3 mr-1" />
            Criar
          </Button>
        </div>
        
        <div className={`space-y-1 ${assistants.length > 10 ? 'max-h-80 overflow-y-auto' : ''}`}>
          {assistants.length > 0 ? (
            assistants.map((assistant) => (
              <Button
                key={assistant.id}
                variant="ghost"
                className="w-full justify-between text-sm font-medium h-9 px-2 text-gray-700 hover:bg-gray-100"
                onClick={() => onAssistantSelect(assistant)}
              >
                <div className="flex items-center min-w-0">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-2">
                    <span className="text-base">{assistant.avatar || '🤖'}</span>
                  </div>
                  <span className="truncate text-left">{assistant.name}</span>
                </div>
                {assistant.visibility && (
                  <div className="flex-shrink-0 ml-2">
                    {assistant.visibility === 'private' && (
                      <Lock className="w-3 h-3 text-gray-400" />
                    )}
                    {assistant.visibility === 'team' && (
                      <Globe className="w-3 h-3 text-gray-400" />
                    )}
                    {assistant.visibility === 'shared' && (
                      <Users className="w-3 h-3 text-gray-400" />
                    )}
                  </div>
                )}
              </Button>
            ))
          ) : (
            <div className="text-xs text-gray-500 text-center py-2">
              Nenhum agente ainda
            </div>
          )}
        </div>
      </div>

      
      {/* Seção de Conversas - Com scroll */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-shrink-0 p-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">Todas as Conversas</h3>
            {activeProvider && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-blue-600 hover:bg-blue-50"
                onClick={() => onNewConversation(activeProvider)}
              >
                <Plus className="w-3 h-3 mr-1" />
                Nova
              </Button>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full px-4 pb-4">
          <div className="space-y-1 relative">
            {conversations.length > 0 ? (
              conversations.map((conversation) => (
                <div key={conversation.id} data-conversation-id={conversation.id} className="group relative flex items-center">
                  <Button
                    variant={activeConversationId === conversation.id ? "secondary" : "ghost"}
                    className="w-full justify-start text-xs h-9 px-2 text-gray-700 hover:bg-gray-100 pr-8"
                    onClick={() => onConversationSelect(conversation.id)}
                  >
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-2">
                      {conversation.aiId === 'openai' ? (
                        <img src="/gpt-logo.png?v=1" alt="GPT Logo" className="w-4 h-4 object-contain" />
                      ) : conversation.aiId === 'anthropic' ? (
                        <img src="/claude-logo.png?v=1" alt="Claude Logo" className="w-4 h-4 object-contain" />
                      ) : conversation.aiId === 'google' ? (
                        <img src="/gemini-logo.png?v=1" alt="Gemini Logo" className="w-4 h-4 object-contain" />
                      ) : conversation.aiId === 'deepseek' ? (
                        <img src="/deepseek-logo.png" alt="DeepSeek Logo" className="w-4 h-4" />
                      ) : (
                        <span className="text-xs">AI</span>
                      )}
                    </div>
                    <div className="flex-1 flex items-center justify-between min-w-0">
                      <span className="truncate text-left">{conversation.title}</span>
                      {conversation.messageCount !== undefined && conversation.messageCount > 0 && (
                        <span className="ml-2 text-[10px] text-gray-400 flex-shrink-0">
                          {conversation.messageCount}
                        </span>
                      )}
                    </div>
                  </Button>
                  
                  {/* Menu de 3 pontinhos - Aparece no hover */}
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === conversation.id ? null : conversation.id);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all relative z-[102]"
                      title="Mais opções"
                    >
                      <MoreVertical className="w-3 h-3" />
                    </button>
                  </div>
                  
                  {/* Menu Dropdown */}
                  {openMenuId === conversation.id && (
                    <>
                      {/* Backdrop para fechar o menu */}
                      <div 
                        className="fixed inset-0"
                        style={{ zIndex: 9998 }}
                        onClick={() => setOpenMenuId(null)}
                      />
                      
                      {/* Menu */}
                      <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-xl border border-gray-200 py-1" style={{ zIndex: 9999 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            onDeleteConversation(conversation.id);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Excluir</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-500 text-center py-4">
                Nenhuma conversa ainda
              </div>
            )}
          </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}